/* =========================================================
   Site detail — three views.
     A "Pestañas" horizontal section tabs (default; red-count
                 badge per tab)
     B "Tablero"  sticky section rail + stacked sections
     C "Dividida" split pane: org's sites left, detail right

   Site Manager is read-only everywhere: no edit affordance,
   a "Solo lectura" chip in its place.
   ========================================================= */
import React, { useEffect, useReducer, useState, type ReactNode } from 'react';
import type { Organization, Role, Site } from '@/types';
import { ic } from '@/components/Icons';
import {
  CajaPendientesSite, EmptyState, FieldLegend, FieldRow, PageHeader, ProgressBar, Section,
  VariantSwitch, estiloPestana, puntoPestana,
} from '@/components/ui';
import { COUNTRIES, ds } from '@/data';
import { useI18n } from '@/i18n';
import { camposDeSeccion, siteSchema, validateField } from '@/lib/fields';
import { progresoSite } from '@/lib/progresoPortal';
import { useGo, type GoFn } from '@/hooks/useRoute';
import { useAppStore } from '@/store/useAppStore';
import { usePuedeEditar, type PermisoEdicion } from '@/hooks/usePuedeEditar';
import { SiteInstructors } from './SiteInstructors';

const SECTIONS = [
  { k: 'identifiers', tone: 'var(--st-lock)' },
  { k: 'site_data', tone: 'var(--st-edit)' },
  { k: 'atc_director', tone: 'var(--st-edit)' },
  { k: 'site_admin', tone: 'var(--st-edit)' },
  { k: 'site_manager', tone: 'var(--st-edit)' },
];

/** El borrador de una ficha en edición.
 *
 *  Los valores son texto salvo los campos «multi» —industria, portafolio,
 *  entorno de formación— que son listas de códigos. */
type Draft = Record<string, string | string[] | undefined>;

interface DetailProps {
  draft: Draft;
  setField: (k: string, v: string | string[]) => void;
  editing: boolean;
  setEditing: (v: boolean) => void;
  save: () => void;
  role: Role;
  site: Site;
  org: Organization;
  allSites: Site[];
  currentId: string;
  go: GoFn;
  /** Si el servidor va a rechazar el guardado, no se ofrece el botón. */
  permiso: PermisoEdicion;
}


const redsIn = (draft: Draft, sectionKey: string) =>
  siteSchema.filter(
    (f) => f.section === sectionKey && f.state === 'required' && (!draft[f.key] || draft[f.key] === '0000'),
  ).length;

function EditActions({ editing, setEditing, save, permiso }: Pick<DetailProps, 'editing' | 'setEditing' | 'save' | 'permiso'>) {
  const { lang, t } = useI18n();
  if (editing) {
    return (
      <>
        <button className="btn" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
        <button className="btn primary" onClick={save}>{ic.save} {t('common.save')}</button>
      </>
    );
  }
  if (!permiso.permanente) {
    return (
      <button
        className="btn primary"
        onClick={() => setEditing(true)}
        disabled={!permiso.puede}
        data-tip={permiso.motivo || undefined}
      >
        {ic.edit} {t('common.edit')}
      </button>
    );
  }
  return (
    <span className="chip lock" data-tip={permiso.motivo || undefined}>
      {ic.eye} {lang === 'es' ? 'Solo lectura' : 'View only'}
    </span>
  );
}

function SectionBlock({
  sectionKey, draft, onChange, editing, tone, right, siteId, onExcusar,
}: {
  sectionKey: string;
  draft: Draft;
  onChange: (k: string, v: string | string[]) => void;
  editing: boolean;
  tone?: string;
  right?: ReactNode;
  /** De qué site son estos campos, para poder marcarlos «no aplica». */
  siteId: string;
  onExcusar?: () => void;
}) {
  const { t } = useI18n();
  return (
    <Section title={t('sec.' + sectionKey)} tone={tone} right={right}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1, marginTop: 8 }}>
        {camposDeSeccion(siteSchema, sectionKey).map((f) => (
          <FieldRow
            key={f.key}
            field={f}
            value={draft[f.key]}
            onChange={onChange}
            editing={editing}
            entidad="site"
            id={siteId}
            onExcusar={onExcusar}
          />
        ))}
      </div>
    </Section>
  );
}

/* ---------- A: tabs (default) ---------- */

function SiteDetailA(p: DetailProps) {
  const { lang, t } = useI18n();
  // el porcentaje del site: su ficha en curso y la de sus instructores
  // marcar «no aplica» no toca el borrador: este contador obliga a la
  // barra a recalcular
  const [, recalcular] = useReducer((n: number) => n + 1, 0);
  const c = progresoSite(p.site.SiteId, p.draft);
  const faltan = c.total - c.hechos;
  const [tab, setTab] = useState('identifiers');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {t('common.completeness')}
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span
              className="mono"
              style={{
                fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em',
                color: c.pct === 100 ? 'var(--ok)' : c.pct >= 70 ? 'var(--brand-primary)' : 'var(--err)',
              }}
            >
              {c.pct}%
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {faltan > 0 ? `${faltan} ${lang === 'es' ? 'pendientes' : 'pending'}` : lang === 'es' ? 'sin pendientes' : 'no pending'}
            </span>
          </div>
          <div style={{ marginTop: 8 }}><ProgressBar pct={c.pct} /></div>
        </div>
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {lang === 'es' ? 'Organización' : 'Organization'}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{p.org.OrgName}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.site.SiteId} · ATCCSN {p.site.ATCCSN}</span>
            {p.site.SiteId === p.site.PrimarySiteId ? (
              <span className="chip brand" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                {React.cloneElement(ic.star, { size: 11, fill: 'currentColor' })} {t('sites.primary_label')}
              </span>
            ) : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <EditActions editing={p.editing} setEditing={p.setEditing} save={p.save} permiso={p.permiso} />
        </div>
      </div>

      <CajaPendientesSite siteId={p.site.SiteId} borrador={p.draft} />

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--line)', overflowX: 'auto', background: 'var(--surface-2)' }}>
          {SECTIONS.map((s) => {
            const reds = redsIn(p.draft, s.k);
            return (
              <button
                key={s.k}
                role="tab"
                aria-selected={tab === s.k}
                onClick={() => setTab(s.k)}
                className="btn tab"
                style={estiloPestana(s.tone, tab === s.k)}
              >
                <span style={puntoPestana(s.tone)} />
                {t('sec.' + s.k)}
                {/* el chip rojo de siempre: sobre el tinte claro se vuelve a
                    ver, que sobre el fondo oscuro anterior desaparecía */}
                {reds > 0 ? (
                  <span className="chip err" style={{ padding: '1px 6px', fontSize: 9 }}>{reds}</span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 18 }}>
          <FieldLegend />
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1 }}>
            {camposDeSeccion(siteSchema, tab).map((f) => (
              <FieldRow
                key={f.key}
                field={f}
                value={p.draft[f.key]}
                onChange={p.setField}
                editing={p.editing}
                entidad="site"
                id={p.site.SiteId}
                onExcusar={recalcular}
              />
            ))}
          </div>
        </div>
      </div>

      <SiteInstructors siteId={p.site.SiteId} />
    </div>
  );
}

/* ---------- B: board ---------- */

function SiteDetailB(p: DetailProps) {
  const { lang, t } = useI18n();
  // el porcentaje del site: su ficha en curso y la de sus instructores
  // marcar «no aplica» no toca el borrador: este contador obliga a la
  // barra a recalcular
  const [, recalcular] = useReducer((n: number) => n + 1, 0);
  const c = progresoSite(p.site.SiteId, p.draft);
  const faltan = c.total - c.hechos;

  return (
    <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16 }}>
      <aside className="m-unstick" style={{ position: 'sticky', top: 80, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
            {lang === 'es' ? 'Secciones' : 'Sections'}
          </div>
          {SECTIONS.map((s) => {
            const reds = redsIn(p.draft, s.k);
            return (
              <button
                key={s.k}
                onClick={() => document.getElementById('sec-' + s.k)?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                className="btn ghost"
                style={{ justifyContent: 'space-between', padding: '7px 10px', width: '100%', fontSize: 12, gap: 8 }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 99, background: s.tone, flexShrink: 0 }} />
                  {t('sec.' + s.k)}
                </span>
                {reds > 0 ? <span className="chip err" style={{ padding: '1px 6px', fontSize: 9 }}>{reds}</span> : null}
              </button>
            );
          })}
        </div>

        <div className="card" style={{ padding: 12 }}>
          <ProgressBar pct={c.pct} label={t('common.completeness')} sub={faltan > 0 ? `${faltan} ${lang === 'es' ? 'pendientes' : 'pending'}` : 'OK'} />
        </div>

        <CajaPendientesSite siteId={p.site.SiteId} borrador={p.draft} />

        {p.editing ? (
          <>
            <button className="btn primary" onClick={p.save} style={{ justifyContent: 'center' }}>{ic.save} {t('common.save')}</button>
            <button className="btn" onClick={() => p.setEditing(false)} style={{ justifyContent: 'center' }}>{t('common.cancel')}</button>
          </>
        ) : !p.permiso.permanente ? (
          <button
            className="btn primary"
            onClick={() => p.setEditing(true)}
            disabled={!p.permiso.puede}
            data-tip={p.permiso.motivo || undefined}
            style={{ justifyContent: 'center' }}
          >
            {ic.edit} {t('common.edit')}
          </button>
        ) : (
          <span className="chip lock" style={{ justifyContent: 'center' }} data-tip={p.permiso.motivo || undefined}>{ic.eye} {lang === 'es' ? 'Solo lectura' : 'View only'}</span>
        )}
      </aside>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FieldLegend />
        {SECTIONS.map((s) => (
          <div key={s.k} id={'sec-' + s.k}>
            <SectionBlock sectionKey={s.k} draft={p.draft} onChange={p.setField} editing={p.editing} tone={s.tone} siteId={p.site.SiteId} onExcusar={recalcular} />
          </div>
        ))}
        <SiteInstructors siteId={p.site.SiteId} />
      </div>
    </div>
  );
}

/* ---------- C: split pane ---------- */

function SiteDetailC(p: DetailProps) {
  const { lang, t } = useI18n();
  // el porcentaje del site: su ficha en curso y la de sus instructores
  // marcar «no aplica» no toca el borrador: este contador obliga a la
  // barra a recalcular
  const [, recalcular] = useReducer((n: number) => n + 1, 0);
  const c = progresoSite(p.site.SiteId, p.draft);
  const faltan = c.total - c.hechos;
  const country = COUNTRIES.find((x) => x.code === p.site.SiteCountry)?.[lang === 'es' ? 'name_es' : 'name_en'];

  return (
    <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 14 }}>
      <div
        className="card m-unstick"
        style={{ overflow: 'hidden', alignSelf: 'flex-start', position: 'sticky', top: 80, maxHeight: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: 12, borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {lang === 'es' ? 'Sites de la organización' : 'Org sites'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{p.allSites.length} sites</div>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {p.allSites.map((s) => {
            const sc = progresoSite(s.SiteId);
            const active = s.SiteId === p.currentId;
            return (
              <button
                key={s.SiteId}
                onClick={() => p.go('site', { siteId: s.SiteId })}
                className="list-row"
                style={{
                  width: '100%', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 6,
                  padding: 12, borderBottom: '1px solid var(--line)',
                  background: active ? 'var(--brand-primary-l)' : 'transparent',
                  borderLeft: active ? '3px solid var(--brand-primary)' : '3px solid transparent',
                  borderTop: 'none', borderRight: 'none', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {s.SiteId === s.PrimarySiteId ? (
                    <span style={{ color: '#FFB020' }}>{React.cloneElement(ic.star, { size: 12, fill: 'currentColor' })}</span>
                  ) : null}
                  <span style={{ fontSize: 13, fontWeight: active ? 700 : 600, color: active ? 'var(--brand-primary-d)' : 'var(--text)' }}>
                    {s.SiteName}
                  </span>
                </div>
                <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{s.SiteId}</div>
                <ProgressBar pct={sc.pct} />
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{p.site.SiteName}</div>
            <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-3)', marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="mono">{p.site.SiteId}</span>
              <span>·</span>
              <span className="mono">ATCCSN {p.site.ATCCSN}</span>
              <span>·</span>
              <span>{p.site.SiteCity}, {country}</span>
              {p.site.SiteId === p.site.PrimarySiteId ? (
                <>
                  <span>·</span>
                  <span className="chip brand">{t('sites.primary_label')}</span>
                </>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 160 }}>
              <ProgressBar pct={c.pct} label={t('common.completeness')} sub={faltan > 0 ? `${faltan} ${lang === 'es' ? 'pendientes' : 'pending'}` : 'OK'} />
            </div>
            <EditActions editing={p.editing} setEditing={p.setEditing} save={p.save} permiso={p.permiso} />
          </div>
        </div>

        <CajaPendientesSite siteId={p.site.SiteId} borrador={p.draft} />

        <FieldLegend />

        {SECTIONS.slice(0, 2).map((s) => (
          <SectionBlock key={s.k} sectionKey={s.k} draft={p.draft} onChange={p.setField} editing={p.editing} tone={s.tone} siteId={p.site.SiteId} onExcusar={recalcular} />
        ))}

        {/* contact bundles grouped in 3 columns */}
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{lang === 'es' ? 'Contactos del site' : 'Site contacts'}</div>
          </div>
          <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
            {['atc_director', 'site_admin', 'site_manager'].map((sec, i) => (
              <div key={sec} style={{ padding: 14, borderRight: i < 2 ? '1px solid var(--line)' : 'none' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 4, height: 14, background: 'var(--st-edit)', borderRadius: 2 }} />
                  {t('sec.' + sec)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {camposDeSeccion(siteSchema, sec).map((f) => (
                    <FieldRow
                key={f.key}
                field={f}
                value={p.draft[f.key]}
                onChange={p.setField}
                editing={p.editing}
                entidad="site"
                id={p.site.SiteId}
                onExcusar={recalcular}
              />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <SiteInstructors siteId={p.site.SiteId} />
      </div>
    </div>
  );
}

/* ---------- wrapper ---------- */

export function SiteDetail({ siteId }: { siteId: string }) {
  const { lang, t } = useI18n();
  const go = useGo();
  const role = useAppStore((s) => s.role);
  const permiso = usePuedeEditar('site');
  const addToast = useAppStore((s) => s.addToast);
  const variant = useAppStore((s) => s.variant.site);
  const setVariantFor = useAppStore((s) => s.setVariantFor);

  const site = ds.getSite(siteId);
  const org = site ? ds.getOrg(site.OrgId) : undefined;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({ ...site });

  useEffect(() => {
    setDraft({ ...ds.getSite(siteId) });
    setEditing(false);
  }, [siteId]);

  if (!site || !org) {
    return (
      <EmptyState
        icon={ic.site}
        title={lang === 'es' ? 'Site no encontrado' : 'Site not found'}
        action={<button className="btn" onClick={() => go('sites')}>{t('common.back')}</button>}
      />
    );
  }

  function setField(k: string, v: string | string[]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  function save() {
    const reds = siteSchema.filter((f) => f.state === 'required' && validateField(f, draft[f.key]));
    if (reds.length) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se puede guardar' : 'Cannot save',
        body: lang === 'es' ? 'Hay campos obligatorios pendientes.' : 'Required fields pending.',
      });
      return;
    }
    // legacy sentinel: an unmigrated postal code must be replaced before saving
    if (draft.SitePostalCode === '0000') {
      addToast({ kind: 'err', title: lang === 'es' ? 'Código postal inválido' : 'Invalid postal', body: '"0000"' });
      return;
    }
    ds.updateSite(siteId, draft as Partial<Site>);
    setEditing(false);
    addToast({ kind: 'save', celebrate: true, title: lang === 'es' ? 'Cambios guardados' : 'Changes saved', body: site!.SiteName });
  }

  const allSites = ds.listSites({ orgId: org.OrgId });
  const props: DetailProps = {
    draft, setField, editing, setEditing, save, role, site, org, allSites,
    currentId: siteId, go, permiso,
  };

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={site.SiteName}
        sub={site.SiteId}
        crumbs={[
          { label: t('nav.sites'), onClick: () => go('sites') },
          { label: org.OrgName, onClick: () => go('org', { orgId: org.OrgId }) },
          { label: site.SiteName },
        ]}
        actions={
          <VariantSwitch
            value={variant}
            onChange={(v) => setVariantFor('site', v)}
            options={[
              { value: 'A', es: 'Pestañas', en: 'Tabs' },
              { value: 'B', es: 'Tablero', en: 'Board' },
              { value: 'C', es: 'Dividida', en: 'Split' },
            ]}
          />
        }
      />
      {variant === 'A' ? <SiteDetailA {...props} /> : variant === 'B' ? <SiteDetailB {...props} /> : <SiteDetailC {...props} />}
    </div>
  );
}
